import { useApolloClient } from '@apollo/client';
import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import type { ActivityListener, ActivityLog } from './activity-log';
import { evictEventsFromCache } from './events-cache';
import { store } from './factory';
import { Store } from './store';

/*
 * Shape of a frame on the events socket. Measured live, events arrive as `frontend_broadcast`, one
 * frame per event; the older names are kept in case the server still sends them anywhere.
 */
interface Frame {
  type?:
    | 'activity_log'
    | 'command_center_broadcast'
    | 'command_center_login'
    | 'command_center_res'
    | 'frontend_broadcast';
  success?: boolean;
  data?:
    | { eventId: string; log: ActivityLog }
    | {
        action: 'create' | 'delete' | 'update';
        eventIds: string[];
        /* Added later than `eventIds`, and still absent on some frames — always treat it as optional. */
        events?: SocketEvent[];
        clientId?: number;
      }
    | { action: 'create-untitled' | 'update-untitled'; assetIds: string[] };
}

/*
 * What a frame can say about a changed event, so a listener can update itself instead of asking the
 * API what happened. Only the fields the server sends: no thumbnail, no start/end time, no priority.
 */
export interface SocketEvent {
  id: string;
  /* Scheduler status: "1" upcoming, "2" live, "3" past. */
  status?: string;
  title?: string;
  organization_name?: string;
  /* Video source — Mux while live, Backblaze once finished. Absent until there is one. */
  downloadUrl?: string;
}

/*
 * `events`: ids that were created or changed, batched. `details` carries the frames' `events[]` for the
 * ids that came with one, and `removedIds` the deleted ones — a listener that can work from those
 * alone never has to ask the API. Ids missing from `details` are the tolerant case: their frame was the
 * older id-only shape, so a listener must still fetch to learn what changed.
 * `resync`: the connection came back after dropping; updates may have been missed while it was down.
 * `untitled`: untitled live assets changed.
 */
export type EventsSocketMessage =
  | { kind: 'events'; ids: number[]; details: Map<number, SocketEvent>; removedIds: number[] }
  | { kind: 'resync' }
  | { kind: 'untitled' };

type Listener = (message: EventsSocketMessage) => void;

/* What a scope follows, so a change marks dirty only the unmounted scopes it concerns. */
export type EventsSocketTopic = 'events' | 'untitled';

export interface EventsSocket {
  /*
   * Starts listening for `scope` — one per distinct list query — and returns the unsubscribe. A scope
   * is remembered after its listener leaves, which is what lets it be marked dirty while unmounted.
   */
  subscribe: (scope: string, listener: Listener, topic?: EventsSocketTopic) => () => void;
  /* Whether `scope` missed updates since it last looked, clearing the flag in the same call. */
  consumeDirty: (scope: string) => boolean;
  /*
   * Activity log entries, for every event — the listener keeps the ones it cares about. Delivered as
   * they arrive, outside the batch window; nothing is marked dirty because nothing can be refetched.
   */
  subscribeActivity: (listener: ActivityListener) => () => void;
}

/*
 * How long ids are collected before they go out as one batch. Measured from the first id and never
 * extended by later ones: a debounce would never fire during a long burst, which is exactly when the
 * screen most needs updating.
 */
const WEBSOCKET_BATCH_MS = 1000;

const Context = createContext<EventsSocket | undefined>(undefined);

/*
 * The one events socket for the whole Command Center. It used to be opened inside every events list
 * hook, so each consumer held its own connection and sent its own login, and the socket only lived
 * while an events page was mounted.
 *
 * Nothing is fetched here. A list that is on screen receives the batch and fetches what it needs; a
 * list that is not is only marked dirty, and reconciles once when it comes back. The backend works
 * only while someone is looking.
 */
export const EventsSocketProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const client = useApolloClient();
  const socketUrl = store(({ environment }) => environment.EVENTS_WEBSOCKET);
  const token = Store.Token.useToken();

  /* No token, no connection: there is nothing to receive before login. */
  const { lastMessage, readyState, sendMessage } = useWebSocket(token ? socketUrl : null, {
    shouldReconnect: (): boolean => true,
    reconnectInterval: 5000,
    reconnectAttempts: 60
  });

  const listeners = useRef(new Map<string, Set<Listener>>());
  const activityListeners = useRef(new Set<ActivityListener>());
  const known = useRef(new Map<string, EventsSocketTopic>());
  const dirty = useRef(new Set<string>());
  const pendingIds = useRef(new Set<number>());
  /* The `events[]` entry of each pending id, when its frame carried one; a later frame overwrites it. */
  const pendingDetails = useRef(new Map<number, SocketEvent>());
  const pendingRemoved = useRef(new Set<number>());
  const pendingUntitled = useRef(false);
  const flushTimer = useRef<ReturnType<typeof setTimeout>>();
  const connected = useRef(false);
  const lostConnection = useRef(false);

  /* The frame already handled, so a re-render with the same `lastMessage` is not processed twice. */
  const lastProcessed = useRef<MessageEvent>();

  const emit = useCallback((message: EventsSocketMessage): void => {
    listeners.current.forEach((set) => {
      set.forEach((listener) => {
        listener(message);
      });
    });
  }, []);

  const markUnmountedDirty = useCallback((topic: EventsSocketTopic): void => {
    known.current.forEach((scopeTopic, scope) => {
      if (scopeTopic === topic && !listeners.current.has(scope)) {
        dirty.current.add(scope);
      }
    });
  }, []);

  /*
   * Untitled changes share the window. They used to go out one per frame, and each one refetched the
   * whole untitled list from the asset service.
   */
  const queue = useCallback(
    (change: { ids?: number[]; details?: SocketEvent[]; removedIds?: number[]; untitled?: boolean }): void => {
      change.ids?.forEach((id) => pendingIds.current.add(id));
      change.removedIds?.forEach((id) => pendingRemoved.current.add(id));

      change.details?.forEach((event) => {
        const id = Number(event.id);

        if (Number.isFinite(id)) {
          pendingDetails.current.set(id, event);
        }
      });

      if (change.untitled) {
        pendingUntitled.current = true;
      }

      if (flushTimer.current !== undefined) {
        return;
      }

      flushTimer.current = setTimeout(() => {
        flushTimer.current = undefined;

        const batch = [...pendingIds.current];
        const details = new Map(pendingDetails.current);
        const removedIds = [...pendingRemoved.current];
        const untitled = pendingUntitled.current;

        pendingIds.current.clear();
        pendingDetails.current.clear();
        pendingRemoved.current.clear();
        pendingUntitled.current = false;

        if (batch.length || removedIds.length) {
          markUnmountedDirty('events');
          emit({ kind: 'events', ids: batch, details, removedIds });
        }

        if (untitled) {
          markUnmountedDirty('untitled');
          emit({ kind: 'untitled' });
        }
      }, WEBSOCKET_BATCH_MS);
    },
    [emit, markUnmountedDirty]
  );

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(JSON.stringify({ type: 'command_center_login', token: token ?? '' }));
    }
  }, [readyState, token, sendMessage]);

  /*
   * A dropped connection means updates may have been missed by every list, mounted or not, so all of
   * them are marked dirty. Once it is back, lists on screen are told to reconcile.
   */
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      connected.current = true;

      if (lostConnection.current) {
        lostConnection.current = false;
        emit({ kind: 'resync' });
      }

      return;
    }

    if (connected.current && (readyState === ReadyState.CLOSED || readyState === ReadyState.CLOSING)) {
      connected.current = false;
      lostConnection.current = true;

      known.current.forEach((_topic, scope) => {
        dirty.current.add(scope);
      });
    }
  }, [readyState, emit]);

  /*
   * Keyed on the event object rather than its text: two identical frames in a row carry the same
   * string, and a dependency on `lastMessage.data` would silently skip the second.
   */
  useEffect(() => {
    if (!lastMessage || lastMessage === lastProcessed.current) {
      return;
    }

    lastProcessed.current = lastMessage;

    let frame: Frame | undefined;

    try {
      frame = JSON.parse(String(lastMessage.data)) as Frame;
    } catch {
      return;
    }

    const data = frame.success ? frame.data : undefined;

    if (!data) {
      return;
    }

    if ('log' in data) {
      const eventId = Number(data.eventId);

      if (Number.isFinite(eventId)) {
        activityListeners.current.forEach((listener) => {
          listener(eventId, data.log);
        });
      }

      return;
    }

    if ('eventIds' in data && data.eventIds.length) {
      const ids = data.eventIds.map(Number);

      /* Removing costs no request, so it applies straight away whether or not a list is on screen. */
      if (data.action === 'delete') {
        evictEventsFromCache(client, ids);
        /* Told separately from `ids`, so nothing refetches over a deletion it can just drop. */
        queue({ removedIds: ids });
      } else {
        queue({ ids, details: data.events });
      }
    }

    if ('assetIds' in data && data.assetIds.length) {
      queue({ untitled: true });
    }
  }, [lastMessage, client, queue]);

  useEffect(
    () => (): void => {
      if (flushTimer.current !== undefined) {
        clearTimeout(flushTimer.current);
      }
    },
    []
  );

  const subscribe = useCallback((scope: string, listener: Listener, topic: EventsSocketTopic = 'events'): (() => void) => {
    known.current.set(scope, topic);

    const set = listeners.current.get(scope) ?? new Set<Listener>();

    set.add(listener);
    listeners.current.set(scope, set);

    return (): void => {
      set.delete(listener);

      if (!set.size) {
        listeners.current.delete(scope);
      }
    };
  }, []);

  const consumeDirty = useCallback((scope: string): boolean => dirty.current.delete(scope), []);

  const subscribeActivity = useCallback((listener: ActivityListener): (() => void) => {
    activityListeners.current.add(listener);

    return (): void => {
      activityListeners.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    (): EventsSocket => ({ subscribe, consumeDirty, subscribeActivity }),
    [subscribe, consumeDirty, subscribeActivity]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useEventsSocket = (): EventsSocket => {
  const socket = useContext(Context);

  if (!socket) {
    throw new Error('useEventsSocket must be used inside <EventsSocketProvider>.');
  }

  return socket;
};
